import { isWatchlistGroupingAttribute } from './watchlistAttributePresentation';

export const POSITION_SIZING_ATTRIBUTE_CAPABILITIES =
    'position-sizing-attribute-capabilities.v1';
export const POSITION_SIZING_ATTRIBUTE_STATE =
    'pos-sizing-attribute-target-state.v1';
export const POSITION_SIZING_ATTRIBUTE_STATE_REQUEST =
    'position-sizing-attribute-state-request.v1';
export const POSITION_SIZING_WORKSPACE_REQUEST =
    'position-sizing-workspace-request.v1';
export const POSITION_SIZING_WORKSPACE_HYDRATION =
    'pos-sizing-workspace-hydration.v2';
export const POSITION_SIZING_ATTRIBUTE_SETTING_MUTATION =
    'position-sizing-attribute-setting-mutation.v1';
export const POSITION_SIZING_ATTRIBUTE_SWITCH =
    'position-sizing-attribute-switch.v1';
export const POSITION_SIZING_ATTRIBUTE_RECALCULATION =
    'position-sizing-attribute-recalculation.v1';
export const POSITION_SIZING_ATTRIBUTE_CHANGESET =
    'position-sizing-attribute-changeset.v2';
export const POSITION_SIZING_ATTRIBUTE_CALCULATION =
    'position-sizing-attribute-calculation.v3';
export const POSITION_SIZING_ATTRIBUTE_PROVIDER =
    'position-sizing-attribute-target-provider.v2';
export const POSITION_SIZING_ATTRIBUTE_PROVIDER_DIGEST =
    '913d60a342cebd55d86eaf7e1a1db1595d2e29b9292df521165f82f892f19f5a';
export const POSITION_SIZING_ATTRIBUTE_PROVIDER_SOURCE =
    'deed325cc997cc11e922c5294f7d2e33ceba92fd';

const isRecord = (value) => Boolean(
    value && typeof value === 'object' && !Array.isArray(value)
);

const finitePositive = (value) => (
    typeof value === 'number' && Number.isFinite(value) && value > 0
);
const EXPECTED_ATTRIBUTE_STATE_BLOCKERS = new Set([
    'no_saved_draft',
    'position_sizing_attribute_input_scope_empty',
    'position_sizing_attribute_input_scope_invalid',
    'position_sizing_attribute_watchlist_stale',
    'position_sizing_durable_context_environment_mismatch',
    'position_sizing_durable_context_invalid',
    'position_sizing_durable_context_pointer_invalid',
    'position_sizing_durable_context_unavailable',
    'position_sizing_durable_context_unknown_legacy',
]);

export function attributeOptionKey(attributeDefinitionId) {
    return attributeDefinitionId == null ? '' : String(attributeDefinitionId);
}

export function attributeDefinitionIdFromOption(value) {
    return String(value);
}

export function isPositionSizingAttributeOption(option) {
    return isWatchlistGroupingAttribute(option);
}

export function positionSizingAttributeOptions(state) {
    return (state?.targets ?? []).filter(isPositionSizingAttributeOption);
}

export function groupLocatorKey(group) {
    if (!isRecord(group)) return '';
    return JSON.stringify([
        group.attribute_definition_id ?? null,
        group.group_kind ?? '',
        group.group_value_key ?? null,
        group.group_value ?? null,
    ]);
}

export function formatPositionSizingGroupLabel(group) {
    return String(group?.group_value ?? '').trim();
}

export function requirePositionSizingAttributeCapability(value) {
    if (
        !isRecord(value)
        || value.contract !== POSITION_SIZING_ATTRIBUTE_CAPABILITIES
        || typeof value.enabled !== 'boolean'
        || typeof value.available !== 'boolean'
        || !isRecord(value.provider)
        || value.provider.contract !== POSITION_SIZING_ATTRIBUTE_PROVIDER
        || value.provider.contract_digest !== POSITION_SIZING_ATTRIBUTE_PROVIDER_DIGEST
        || value.provider.source_revision !== POSITION_SIZING_ATTRIBUTE_PROVIDER_SOURCE
        || typeof value.provider.configured !== 'boolean'
        || value.available !== (value.enabled && value.provider.configured)
    ) {
        throw new Error('Generic Position Sizing capability is invalid.');
    }
    return value;
}

function requireGroup(group) {
    if (
        !isRecord(group)
        || !['assigned', 'unassigned', 'whole_watchlist'].includes(group.group_kind)
        || !Object.prototype.hasOwnProperty.call(group, 'attribute_definition_id')
        || !Object.prototype.hasOwnProperty.call(group, 'group_value')
        || !Object.prototype.hasOwnProperty.call(group, 'group_value_key')
    ) {
        throw new Error('Position Sizing attribute group is invalid.');
    }
    return group;
}

function requireSetting(setting, { allowUnpersisted = false } = {}) {
    if (
        !isRecord(setting)
        || !Number.isInteger(setting.version)
        || setting.version < (allowUnpersisted ? 0 : 1)
        || !finitePositive(setting.target_vol_pct)
        || !finitePositive(setting.target_vol_dollar)
        || !finitePositive(setting.scaler)
    ) {
        throw new Error('Position Sizing attribute setting is invalid.');
    }
    requireGroup(setting.group);
    return setting;
}

export function requirePositionSizingAttributeSettingMutation(value) {
    if (
        !isRecord(value)
        || value.contract !== POSITION_SIZING_ATTRIBUTE_STATE
        || value.recalculation_required !== true
    ) {
        throw new Error('Position Sizing attribute setting response is invalid.');
    }
    requireSetting(value.setting);
    return value;
}

export function requirePositionSizingAttributeState(value) {
    const admission = value?.calculation_admission;
    const hasPortfolio = value?.portfolio_id != null;
    if (
        !isRecord(value)
        || value.contract !== POSITION_SIZING_ATTRIBUTE_STATE
        || (
            hasPortfolio
            && (
                !Number.isInteger(value.portfolio_id)
                || value.portfolio_id < 1
                || !Number.isInteger(value.target_version)
                || value.target_version < 1
            )
        )
        || !Array.isArray(value.targets)
        || !isRecord(value.watchlist)
        || !Array.isArray(value.watchlist.definitions)
        || (
            hasPortfolio
            && (
                !Array.isArray(value.watchlist.values)
                || !Array.isArray(value.watchlist.cognive_ids)
            )
        )
        || !isRecord(admission)
        || typeof admission.can_calculate !== 'boolean'
        || (
            admission.can_calculate
            && admission.blocker !== null
        )
        || (
            !admission.can_calculate
            && (
                !isRecord(admission.blocker)
                || !EXPECTED_ATTRIBUTE_STATE_BLOCKERS.has(admission.blocker.code)
                || admission.blocker.action !== 'sync_draft'
                || typeof admission.blocker.message !== 'string'
                || !admission.blocker.message.trim()
            )
        )
    ) {
        throw new Error('Position Sizing attribute state is invalid.');
    }
    if (!hasPortfolio) {
        if (
            admission.can_calculate
            || value.portfolio_id !== null
            || value.target_kind !== null
            || value.target_attribute_definition_id !== null
            || value.target_version !== null
            || value.targets.length !== 0
        ) {
            throw new Error('Blocked Position Sizing attribute state is invalid.');
        }
        return value;
    }
    const cogniveIds = new Set(value.watchlist.cognive_ids);
    const definitionIds = new Set(value.watchlist.definitions.map(
        (definition) => definition?.attribute_definition_id,
    ));
    if (
        cogniveIds.size !== value.watchlist.cognive_ids.length
        || definitionIds.size !== value.watchlist.definitions.length
        || [...cogniveIds].some((cogniveId) => (
            typeof cogniveId !== 'string' || !cogniveId
        ))
        || [...definitionIds].some((definitionId) => (
            typeof definitionId !== 'string' || !definitionId
        ))
        || value.watchlist.values.some((item) => (
            !isRecord(item)
            || !cogniveIds.has(item.cognive_id)
            || !definitionIds.has(item.attribute_definition_id)
            || typeof item.value_text !== 'string'
            || !item.value_text
            || typeof item.value_key !== 'string'
            || !item.value_key
        ))
    ) {
        throw new Error('Position Sizing Watchlist attribute snapshot is invalid.');
    }
    const optionKeys = new Set();
    value.targets.forEach((target) => {
        if (
            !isRecord(target)
            || target.target_kind !== 'attribute'
            || typeof target.attribute_definition_id !== 'string'
            || !target.attribute_definition_id
            || typeof target.display_name !== 'string'
            || !Array.isArray(target.group_settings)
        ) {
            throw new Error('Position Sizing attribute target option is invalid.');
        }
        const key = attributeOptionKey(target.attribute_definition_id);
        if (optionKeys.has(key)) {
            throw new Error('Position Sizing attribute target options are duplicated.');
        }
        optionKeys.add(key);
        const groupKeys = new Set();
        target.group_settings.forEach((setting) => {
            // Version zero is the read-only catalog representation of a group
            // that will be inserted on its first explicit edit.
            requireSetting(setting, { allowUnpersisted: true });
            if (setting.group.group_kind !== 'assigned') {
                throw new Error('Absent Position Sizing attribute values are not categories.');
            }
            const groupKey = groupLocatorKey(setting.group);
            if (groupKeys.has(groupKey)) {
                throw new Error('Position Sizing attribute settings are duplicated.');
            }
            groupKeys.add(groupKey);
        });
    });
    const zeroAttributeState = (
        value.targets.length === 0
        && value.watchlist.definitions.length === 0
        && value.target_kind === 'whole_watchlist'
        && value.target_attribute_definition_id == null
    );
    if (
        !zeroAttributeState
        && (
            value.target_kind !== 'attribute'
            || !optionKeys.has(attributeOptionKey(value.target_attribute_definition_id))
        )
    ) {
        throw new Error('Active Position Sizing attribute target is unavailable.');
    }
    return value;
}

export function requirePositionSizingAttributeCalculation(value) {
    if (
        !isRecord(value)
        || value.contract !== POSITION_SIZING_ATTRIBUTE_CALCULATION
        || !isRecord(value.target)
        || !Array.isArray(value.pos_sizing)
        || !Array.isArray(value.target_groups)
        || !Array.isArray(value.dropped_cognive_ids_from_calc)
    ) {
        throw new Error('Generic Position Sizing calculation is invalid.');
    }
    value.target_groups.forEach((row) => {
        requireGroup(row?.target_group);
        if (row.target_group.group_kind !== 'assigned') {
            throw new Error('Absent Position Sizing target values are not categories.');
        }
    });
    const savedProjection = Object.keys(value.target).length === 0;
    if (savedProjection) {
        if (value.target_groups.length > 0) {
            throw new Error('Saved Position Sizing projection cannot contain target groups.');
        }
    } else if (
        !['attribute', 'whole_watchlist'].includes(value.target.target_kind)
        || !Number.isInteger(value.target.target_version)
        || value.target.target_version < 1
        || (
            value.target.target_kind === 'attribute'
            && (
                typeof value.target.target_attribute_definition_id !== 'string'
                || !value.target.target_attribute_definition_id
            )
        )
        || (
            value.target.target_kind === 'whole_watchlist'
            && value.target.target_attribute_definition_id != null
        )
    ) {
        throw new Error('Generic Position Sizing calculation target is invalid.');
    }
    return value;
}

export function requirePositionSizingWorkspaceHydration(value) {
    if (
        !isRecord(value)
        || value.hydration_contract !== POSITION_SIZING_WORKSPACE_HYDRATION
    ) {
        throw new Error('Position Sizing workspace hydration is invalid.');
    }
    const capability = requirePositionSizingAttributeCapability(
        value.attribute_capability,
    );
    const state = requirePositionSizingAttributeState(value.attribute_state);
    const calculation = requirePositionSizingAttributeCalculation(value);
    if (capability.available !== true || state.portfolio_id == null) {
        throw new Error('Position Sizing workspace hydration is inconsistent.');
    }
    if (
        state.calculation_admission?.can_calculate === true
        && (
            calculation.target.target_kind !== state.target_kind
            || attributeOptionKey(
                calculation.target.target_attribute_definition_id,
            ) !== attributeOptionKey(state.target_attribute_definition_id)
            || calculation.target.target_version !== state.target_version
        )
    ) {
        throw new Error('Position Sizing workspace calculation target is inconsistent.');
    }
    return {
        ...value,
        attribute_capability: capability,
        attribute_state: state,
        attribute_calculation: calculation,
    };
}

export function activeTargetOption(state) {
    const activeKey = attributeOptionKey(state?.target_attribute_definition_id);
    return state?.targets?.find(
        (target) => attributeOptionKey(target.attribute_definition_id) === activeKey,
    ) ?? null;
}

export function targetOptionByKey(state, optionKey) {
    return state?.targets?.find(
        (target) => attributeOptionKey(target.attribute_definition_id) === optionKey,
    ) ?? null;
}

export function defaultSummaryOptionKey(state, storedKey = '') {
    const options = positionSizingAttributeOptions(state);
    if (options.some(
        (target) => attributeOptionKey(target.attribute_definition_id) === storedKey,
    )) {
        return storedKey;
    }
    const definitions = state?.watchlist?.definitions ?? [];
    const availableKey = (definition) => {
        const key = attributeOptionKey(definition?.attribute_definition_id);
        return options.some(
            (target) => attributeOptionKey(target.attribute_definition_id) === key,
        ) ? key : '';
    };
    const firstEditableUser = definitions.find(
        (definition) => definition.source_kind === 'user' && definition.editable === true,
    );
    const firstEditableUserKey = availableKey(firstEditableUser);
    if (firstEditableUserKey) {
        return firstEditableUserKey;
    }
    const country = definitions.find(
        (definition) => definition.system_key === 'country',
    );
    const countryKey = availableKey(country);
    if (countryKey) {
        return countryKey;
    }
    return options.length > 0
        ? attributeOptionKey(options[0].attribute_definition_id)
        : '';
}

export function buildPositionSizingTargetRows(state, calculation, optionKey) {
    const option = targetOptionByKey(state, optionKey);
    if (!option) return [];
    const calculationMatches = (
        attributeOptionKey(calculation?.target?.target_attribute_definition_id)
        === optionKey
    );
    const metricsByGroup = new Map(
        (calculationMatches ? calculation.target_groups : []).map(
            (row) => [groupLocatorKey(row.target_group), row],
        ),
    );
    return option.group_settings.filter(
        (setting) => setting.group.group_kind === 'assigned',
    ).map((setting) => {
        const metrics = metricsByGroup.get(groupLocatorKey(setting.group)) ?? {};
        return {
            ...metrics,
            ...setting,
            id: groupLocatorKey(setting.group),
            category: formatPositionSizingGroupLabel(setting.group),
            member_count: metrics.member_count ?? null,
            pos_gmv_dollar:
                metrics.pos_gmv_dollar ?? metrics.model_gmv_2_dollar ?? null,
        };
    });
}

export function buildPositionSizingSummaryRows(state, calculation, optionKey) {
    const groupsByCognive = new Map(
        (state?.watchlist?.values ?? []).filter(
            (item) => item.attribute_definition_id === optionKey,
        ).map((item) => [
            item.cognive_id,
            {
                attribute_definition_id: item.attribute_definition_id,
                group_kind: 'assigned',
                group_value: item.value_text,
                group_value_key: item.value_key,
            },
        ]),
    );
    const grouped = new Map();
    (calculation?.pos_sizing ?? []).forEach((row) => {
        const group = groupsByCognive.get(row?.cognive_id);
        if (!group) return;
        const key = groupLocatorKey(group);
        const current = grouped.get(key) ?? {
            id: key,
            group,
            category: formatPositionSizingGroupLabel(group),
            count: 0,
            gmv: 0,
            net_rank: 0,
            exp_dollar: 0,
            beta_net_exp: 0,
            pos_size_exp_dollar: 0,
            idio_vol_dollar: 0,
            edge_dollar: 0,
        };
        const positionGmv = Number(row.pos_gmv_dollar);
        const betaNetExposure = Number(row.beta) * Number(row.idio_var_pct || 0);
        current.count += 1;
        current.gmv += Math.abs(positionGmv);
        current.net_rank += Number(row.trade_idea);
        current.exp_dollar += positionGmv;
        current.beta_net_exp += betaNetExposure;
        current.pos_size_exp_dollar += positionGmv;
        current.idio_vol_dollar += Number(row.idio_vol_dollar);
        current.edge_dollar += Number(row.edge_dollar);
        grouped.set(key, current);
    });
    return [...grouped.values()]
        .sort((left, right) => left.id.localeCompare(right.id))
        .map((row) => ({
            ...row,
            pct_gmv_exp_dollar: row.gmv === 0
                ? 0
                : row.exp_dollar / row.gmv * 100,
            pct_gmv_beta_exp: row.gmv === 0
                ? 0
                : row.beta_net_exp / row.gmv * 100,
        }));
}

export function positionSizingAttributeDefaults({
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
}) {
    return {
        default_target_vol_pct: defaultTargetVolPct,
        default_target_vol_dollar: defaultTargetVolDollar,
        default_scaler: defaultScaler,
    };
}
